/**
 * F-301 — Texas City Override admin (AC-1 / AC-2 / AC-3).
 *
 * Texas-market admin surface (registered under /texas/city-overrides).
 * Shows the effective/resolved rule set for a selected city and lets a
 * tenant (or super-admin tenant) create SUPPLEMENT / REPLACE / RESTRICT
 * overrides against the statewide F-301 base registry.
 *
 * Contract via cityOverridesApi — server-side tenant scoping from the JWT;
 * every call is fail-soft, so the page renders partial data instead of
 * crashing when the store is quiet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import cityOverridesApi, {
    BaseRule,
    CityOverride,
    CityOverrideMeta,
    OverrideAction,
    OverrideLevel,
    ResolvedCitySet,
} from '../../services/cityOverridesApi';

export const OverrideActionMeta: Record<OverrideAction, { label: string; blurb: string }> = {
    SUPPLEMENT: { label: 'Supplement', blurb: 'base rule stays + city rule is added' },
    REPLACE: { label: 'Replace', blurb: 'base rule is swapped wholesale for the city rule' },
    RESTRICT: { label: 'Restrict', blurb: 'base rule is blocked from the output' },
};

const FALLBACK_ACTIONS: OverrideAction[] = ['SUPPLEMENT', 'REPLACE', 'RESTRICT'];
const FALLBACK_LEVELS: OverrideLevel[] = ['federal', 'state', 'county', 'city', 'district'];

export const CityOverridesAdmin: React.FC = () => {
    const [meta, setMeta] = useState<CityOverrideMeta | null>(null);
    const [baseRules, setBaseRules] = useState<BaseRule[]>([]);
    const [overrides, setOverrides] = useState<CityOverride[]>([]);
    const [resolved, setResolved] = useState<ResolvedCitySet | null>(null);
    const [requiredFields, setRequiredFields] = useState<string[]>([]);

    const [city, setCity] = useState('');
    const [resolveCity, setResolveCity] = useState('');

    const [stateRuleId, setStateRuleId] = useState('');
    const [action, setAction] = useState<OverrideAction>('SUPPLEMENT');
    const [level, setLevel] = useState<OverrideLevel>('city');
    const [payloadText, setPayloadText] = useState('');
    const [ordinanceRef, setOrdinanceRef] = useState('');
    const [notes, setNotes] = useState('');

    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const refreshOverrides = useCallback(async (scope?: string) => {
        setOverrides(await cityOverridesApi.listOverrides(scope));
    }, []);

    useEffect(() => {
        (async () => {
            const [metaRes, rulesRes, overridesRes] = await Promise.allSettled([
                cityOverridesApi.getMeta(),
                cityOverridesApi.getBaseRules(),
                cityOverridesApi.listOverrides(),
            ]);
            if (metaRes.status === 'fulfilled') setMeta(metaRes.value);
            if (rulesRes.status === 'fulfilled') setBaseRules(rulesRes.value);
            if (overridesRes.status === 'fulfilled') setOverrides(overridesRes.value);
        })();
    }, [refreshOverrides]);

    const resolve = async (targetOverride?: string) => {
        const target = (targetOverride ?? resolveCity).trim();
        if (!target) {
            setError('Enter a city name to resolve its effective rule set.');
            return;
        }
        setBusy('resolve');
        setError(null);
        setSuccess(null);
        try {
            const [setRes, requiredRes] = await Promise.allSettled([
                cityOverridesApi.resolveCity(target),
                cityOverridesApi.requiredFields(target),
            ]);
            if (setRes.status === 'fulfilled') setResolved(setRes.value);
            if (requiredRes.status === 'fulfilled') setRequiredFields(requiredRes.value?.required || []);
            setSuccess(`Resolved ${target}: ${setRes.status === 'fulfilled' && setRes.value ? `${setRes.value.effectiveRules.length} effective rule(s), ${setRes.value.suppressed.length} restricted.` : 'nothing returned.'}`);
        } finally {
            setBusy(null);
        }
    };

    const createOverride = async () => {
        setError(null);
        setSuccess(null);
        const trimmedCity = city.trim();
        setBusy('create');
        try {
            if (!trimmedCity) return setError('City is required.');
            if (!stateRuleId) return setError('State rule is required.');
            let payload: Record<string, unknown> | null = null;
            if (action !== 'RESTRICT') {
                const text = payloadText.trim();
                if (!text) return setError(`A payload object is required for ${action}.`);
                try {
                    payload = JSON.parse(text);
                    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
                        return setError('Payload must be a JSON object.');
                    }
                } catch {
                    return setError('Payload is not valid JSON.');
                }
                if (action === 'REPLACE' && !payload.ruleId) {
                    return setError('payload.ruleId is required for REPLACE.');
                }
            }
            const created = await cityOverridesApi.createOverride({
                city: trimmedCity,
                stateRuleId,
                action,
                level,
                payload,
                ordinanceRef: ordinanceRef.trim() || null,
                notes: notes.trim() || null,
                createdBy: 'texas-admin',
            });
            if (!created) {
                return setError('Override could not be saved — check the field values (validation mirrors the backend).');
            }
            setCity('');
            setStateRuleId('');
            setPayloadText('');
            setOrdinanceRef('');
            setNotes('');
            await refreshOverrides(trimmedCity);
            setResolveCity(trimmedCity);
            await resolve(trimmedCity);
            setSuccess(`Override saved: ${created.city} # ${created.stateRuleId} (${created.action}).`);
        } finally {
            setBusy(null);
        }
    };

    const requiredLabel = (camelKey: string) =>
        baseRules.find((r) => r.camelKey === camelKey)?.label || camelKey;

    return (
        <div className="texas-page-container" data-testid="city-overrides-page">
            <h1>City Override Administration</h1>
            <p className="page-description" data-testid="city-overrides-description">
                Resolve the effective Texas registration rule set (AC-1) against the enforced priority chain (AC-2)
                for any city, and create SUPPLEMENT / REPLACE / RESTRICT overrides against the statewide F-301 base registry.
            </p>

            {success && <div className="success" data-testid="city-overrides-success">{success}</div>}
            {error && <div className="error" data-testid="city-overrides-error">{error}</div>}

            <div className="result-section">
                <h2>Resolve a city</h2>
                <div className="flex flex-wrap items-end gap-3">
                    <label className="flex flex-col text-xs text-gray-600">
                        City
                        <input
                            className="lease-input"
                            value={resolveCity}
                            onChange={(e) => setResolveCity(e.target.value)}
                            placeholder="Austin"
                            data-testid="city-overrides-city-input"
                        />
                    </label>
                    <button className="lease-input-btn" onClick={() => resolve()} disabled={busy === 'resolve'} data-testid="city-overrides-resolve">
                        Resolve effective rules
                    </button>
                </div>

                {resolved && (
                    <div className="result-card" data-testid="city-overrides-resolved">
                        <p>
                            <strong>{resolved.city}</strong> · {resolved.effectiveRules.length} effective rule(s) ·
                            {resolved.applied.length} applied · {resolved.suppressed.length} suppressed
                        </p>
                        <p className="text-gray-500 text-xs">Priority chain: {resolved.chain.join(' → ')}</p>
                        {resolved.applied.length > 0 && (
                            <ul className="text-sm" data-testid="city-overrides-applied">
                                {resolved.applied.map((a) => (
                                    <li key={`${a.ruleId}-${a.overrideId}`} data-testid="city-overrides-applied-row">
                                        {a.ruleId} ← {a.action} by {a.level}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {resolved.suppressed.length > 0 && (
                            <ul className="text-sm red-warning" data-testid="city-overrides-suppressed">
                                {resolved.suppressed.map((s) => (
                                    <li key={s.ruleId} data-testid="city-overrides-suppressed-row">
                                        {s.ruleId} blocked by {s.level}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <table className="w-full text-sm mt-2">
                            <thead>
                                <tr className="text-left text-gray-500 border-b">
                                    <th className="py-2 pr-4">Rule</th>
                                    <th className="py-2 pr-4">Label</th>
                                    <th className="py-2 pr-4">Required</th>
                                    <th className="py-2 pr-4">Level</th>
                                    <th className="py-2">Source</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resolved.effectiveRules.map((r) => (
                                    <tr key={r.ruleId} className="border-b last:border-0" data-testid="city-overrides-effective-row">
                                        <td className="py-2 pr-4 font-mono text-xs">{r.ruleId}</td>
                                        <td className="py-2 pr-4">{r.label}</td>
                                        <td className="py-2 pr-4">{r.required ? <span className="success">required</span> : 'optional'}</td>
                                        <td className="py-2 pr-4">{r.level}</td>
                                        <td className="py-2">{r.source}{r.ordinanceRef ? ` · ${r.ordinanceRef}` : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="text-sm text-gray-700 mt-2" data-testid="city-overrides-required">
                            Required fields for completeness ({requiredFields.length}): {requiredFields.length ? requiredFields.map(requiredLabel).join(', ') : 'none'}
                        </p>
                    </div>
                )}
            </div>

            <div className="result-section">
                <h2>Tenant overrides</h2>
                {overrides.length > 0 ? (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500 border-b">
                                <th className="py-2 pr-4">City</th>
                                <th className="py-2 pr-4">Rule</th>
                                <th className="py-2 pr-4">Action</th>
                                <th className="py-2 pr-4">Level</th>
                                <th className="py-2">Ordinance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {overrides.map((o) => (
                                <tr key={o.overrideId} className="border-b last:border-0" data-testid="city-overrides-list-row">
                                    <td className="py-2 pr-4 font-medium">{o.city}</td>
                                    <td className="py-2 pr-4 font-mono text-xs">{o.stateRuleId}</td>
                                    <td className="py-2 pr-4">{o.action}</td>
                                    <td className="py-2 pr-4">{o.level}</td>
                                    <td className="py-2">{o.ordinanceRef || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="no-results" data-testid="city-overrides-empty">No city overrides on file for this tenant.</p>
                )}
            </div>

            <div className="result-section">
                <h2>Create override</h2>
                <div className="flex flex-wrap items-end gap-3">
                    <label className="flex flex-col text-xs text-gray-600">
                        City
                        <input className="lease-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Austin" data-testid="city-overrides-create-city" />
                    </label>
                    <label className="flex flex-col text-xs text-gray-600">
                        State rule
                        <select
                            className="lease-input"
                            value={stateRuleId}
                            onChange={(e) => setStateRuleId(e.target.value)}
                            data-testid="city-overrides-rule-select"
                        >
                            <option value="">Select a base rule…</option>
                            {baseRules.map((r) => (
                                <option key={r.ruleId} value={r.ruleId}>{r.label} ({r.ruleId})</option>
                            ))}
                        </select>
                    </label>
                    <label className="flex flex-col text-xs text-gray-600">
                        Action
                        <select
                            className="lease-input"
                            value={action}
                            onChange={(e) => setAction(e.target.value as OverrideAction)}
                            data-testid="city-overrides-action-select"
                        >
                            {(meta ? meta.actions : FALLBACK_ACTIONS).map((a) => (
                                <option key={a} value={a}>{OverrideActionMeta[a].label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="flex flex-col text-xs text-gray-600">
                        Level
                        <select
                            className="lease-input"
                            value={level}
                            onChange={(e) => setLevel(e.target.value as OverrideLevel)}
                            data-testid="city-overrides-level-select"
                        >
                            {(meta ? meta.priorityChain : FALLBACK_LEVELS).map((l) => (
                                <option key={l} value={l}>{l}</option>
                            ))}
                        </select>
                    </label>
                </div>
                <p className="text-gray-500 text-xs mt-2">{OverrideActionMeta[action].blurb}</p>
                <div className="flex flex-wrap items-start gap-3 mt-2">
                    <label className="flex flex-col text-xs text-gray-600 min-w-64">
                        Payload (JSON — required for SUPPLEMENT / REPLACE)
                        <textarea
                            className="lease-input"
                            value={payloadText}
                            onChange={(e) => setPayloadText(e.target.value)}
                            placeholder='{"ruleId":"ac-1-austin","label":"Austin Registration","required":true}'
                            rows={3}
                            data-testid="city-overrides-payload"
                        />
                    </label>
                    <label className="flex flex-col text-xs text-gray-600">
                        Ordinance ref
                        <input className="lease-input" value={ordinanceRef} onChange={(e) => setOrdinanceRef(e.target.value)} placeholder="Ord 2026-01" data-testid="city-overrides-ordinance" />
                    </label>
                    <label className="flex flex-col text-xs text-gray-600 min-w-48">
                        Notes
                        <input className="lease-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" data-testid="city-overrides-notes" />
                    </label>
                </div>
                <button className="lease-input-btn mt-3" onClick={createOverride} disabled={busy === 'create'} data-testid="city-overrides-create">
                    Create override
                </button>
            </div>
        </div>
    );
};

export default CityOverridesAdmin;